
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const equipment = [
  {
    name: 'Sprężarka Śrubowa Compaic',
    category: 'Pneumatyka',
    description:
      'Zasilanie narzędzi pneumatycznych i systemów czyszczenia strumieniowego',
    image:
      'https://public.readdy.ai/ai/img_res/1b401bc204d8a5cdaae9981d680f2589.jpg',
  },
  {
    name: 'Maszyna Geka',
    category: 'Cięcie i Wykrawanie',
    description:
      'Uniwersalna maszyna do cięcia, wykrawania i kształtowania profili stalowych',
    image:
      'https://readdy.ai/api/search-image?query=industrial%20ironworker%20punching%20and%20cutting%20machine%20Geka%20brand%20in%20dark%20workshop%20with%20blue%20atmospheric%20lighting%20professional%20industrial%20equipment%20photography%20photorealistic%208k%20quality%20no%20text%20no%20logo&width=600&height=400&seq=equip-geka-001&orientation=landscape',
  },
  {
    name: 'Agregat Malarski Hydrodynamiczny',
    category: 'Zabezpieczenia',
    description: 'Profesjonalne nakładanie powłok antykorozyjnych i malarskich',
    image:
      'https://readdy.ai/api/search-image?query=industrial%20hydrodynamic%20paint%20spraying%20equipment%20airless%20sprayer%20in%20dark%20workshop%20with%20blue%20lighting%20professional%20equipment%20photography%20photorealistic%208k%20quality%20metallic%20finish%20no%20text%20no%20logo&width=600&height=400&seq=equip-paint-001&orientation=landscape',
  },
  {
    name: 'Stanowiska Spawalnicze TIG/MIG/MMA',
    category: 'Spawalnictwo',
    description:
      'Nowoczesne stanowiska do spawania wszystkimi metodami z pełnym wyposażeniem',
    image:
      'https://readdy.ai/api/search-image?query=professional%20TIG%20MIG%20welding%20station%20with%20welding%20machine%20and%20equipment%20in%20dark%20industrial%20workshop%20blue%20electric%20arc%20light%20reflections%20photorealistic%208k%20quality%20cinematic%20lighting%20no%20text%20no%20logo&width=600&height=400&seq=equip-weld-001&orientation=landscape',
  },
  {
    name: 'Tokarka i Frezarka CNC',
    category: 'Obróbka Skrawaniem',
    description: 'Precyzyjna obróbka części z tolerancją do 0.01mm',
    image:
      'https://readdy.ai/api/search-image?query=CNC%20lathe%20and%20milling%20machine%20in%20dark%20industrial%20workshop%20with%20blue%20LED%20lighting%20precision%20metalworking%20equipment%20photorealistic%208k%20quality%20professional%20photography%20no%20text%20no%20logo&width=600&height=400&seq=equip-cnc-001&orientation=landscape',
  },
  {
    name: 'Plazma CNC',
    category: 'Cięcie Termiczne',
    description: 'Cięcie plazmą blach ze sterowaniem numerycznym CNC',
    image:
      'https://readdy.ai/api/search-image?query=CNC%20plasma%20cutting%20machine%20cutting%20thick%20steel%20plate%20with%20bright%20blue%20plasma%20arc%20in%20dark%20industrial%20environment%20photorealistic%208k%20quality%20sparks%20flying%20dramatic%20lighting%20no%20text%20no%20logo&width=600&height=400&seq=equip-plasma-001&orientation=landscape',
  },
];

export default function EquipmentShowcase() {
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="relative py-20 sm:py-32 bg-[#111111] overflow-hidden"
    >
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20industrial%20factory%20floor%20with%20heavy%20machinery%20and%20steel%20structures%20atmospheric%20blue%20lighting%20wide%20angle%20cinematic%20photorealistic%208k%20quality%20moody%20environment%20no%20text%20no%20logo&width=1920&height=1080&seq=equip-bg-001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center opacity-10"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/95 to-[#111111]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16 sm:mb-20"
        >
          <span
            className="text-[#00E5FF] text-xs tracking-[0.3em] uppercase font-semibold"
            style={{ fontFamily: 'Orbitron, sans-serif' }}
          >
            Park Maszynowy
          </span>
          <h2
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mt-4 leading-tight"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
          >
            Nasze <span className="text-[#00E5FF]">Wyposażenie</span>
          </h2>
          <p
            className="text-[#C0C7CF] text-sm sm:text-base mt-4 max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Systematycznie inwestujemy w nowoczesne urządzenia, które czynią nas
            bardziej konkurencyjnymi na rynku konstrukcji stalowych.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {equipment.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: index * 0.1 }}
              className="group relative bg-[#050505] border border-[#1A1A1A] overflow-hidden hover:border-[#00E5FF]/40 transition-all duration-500"
            >
              <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
                <div className="absolute top-3 left-3">
                  <span
                    className="inline-block px-3 py-1 bg-[#050505] bg-opacity-80 backdrop-blur-sm border border-[#00E5FF]/30 text-[#00E5FF] text-[9px] sm:text-[10px] tracking-[0.15em] uppercase font-semibold"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}
                  >
                    {item.category}
                  </span>
                </div>
              </div>

              <div className="p-5 sm:p-6">
                <h3
                  className="text-base sm:text-lg font-bold text-white mb-2 leading-tight group-hover:text-[#00E5FF] transition-colors duration-300"
                  style={{ fontFamily: 'Rajdhani, sans-serif' }}
                >
                  {item.name}
                </h3>
                <p
                  className="text-[#888] text-xs sm:text-sm leading-relaxed"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {item.description}
                </p>
              </div>

              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#00E5FF]/0 to-transparent group-hover:via-[#00E5FF] transition-all duration-500" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
